import { useEffect, useState } from 'react';
import { TimeRange, DashboardData } from './types';

export interface SimulationOptionsPerformance {
  simulation: true;
  storeType: 'chicken_qsr' | 'coffee_shop' | 'italian_restaurant' | 'burger_qsr' | 'dessert_qsr' | 'steak_restaurant' | 'carvery_restaurant' | 'cocktail_bar' | 'pub';
  currentHour: number;
}

const CATEGORY_MAP: Record<SimulationOptionsPerformance['storeType'], 'qsr' | 'coffee' | 'restaurant' | 'bar'> = {
  chicken_qsr: 'qsr', burger_qsr: 'qsr', dessert_qsr: 'qsr', coffee_shop: 'coffee', italian_restaurant: 'restaurant', steak_restaurant: 'restaurant', carvery_restaurant: 'restaurant', cocktail_bar: 'bar', pub: 'bar',
};

// Progress narrative bases (mutable arrays – no readonly inference)
const GOOD_BASE = {
  chicken_qsr: [
    { leading: 'Great prep', text: 'Chicken holding times optimised', details: 'You rotated hot trays on time. Food stayed safe and juicy, and waste stayed low.', improvementOptions: ['Audit holding every 30 mins to cut waste', 'Label trays promptly to reduce waste'] },
    { leading: 'Solid speed', text: 'Drive-thru service time beating peers', details: 'Orders moved quickly at the window. Faster service keeps queues short and brings in more sales.', improvementOptions: ['Keep staging area clear to reduce waste', 'Pre-stage condiments to cut waste'] },
    { leading: 'Consistency up', text: 'Prep yield variance narrowing', details: 'Prep was consistent. You used the right amounts, which cuts waste and makes ordering easier.', improvementOptions: ['Sharpen knives to reduce trim waste', 'Follow cut guide to cut waste'] },
  ],
  burger_qsr: [
    { leading: 'Hot off grill', text: 'Burger build accuracy strong', details: 'Builds matched tickets first time. Fewer remakes saves time and ingredients.', improvementOptions: ['Pre-read tickets aloud to avoid re-makes', 'Stage buns smarter to reduce waste'] },
    { leading: 'Waste down', text: 'Cheese slice waste reduced', details: 'Fewer cheese slices were thrown away. This lowers food cost and keeps stations cold.', improvementOptions: ['Use end-of-stack first to cut waste', 'Refreeze only within policy to reduce waste'] },
    { leading: 'Speed boost', text: 'Guest wait times improved', details: 'Guests waited less at the counter. Smooth handoffs keep lines moving and protect sales.', improvementOptions: ['Cross-train floater to reduce waste', 'Right-size patty batches to cut waste'] },
  ],
  dessert_qsr: [
    { leading: 'Sweet stats', text: 'Topping portion control tight', details: 'Portions were measured well. Consistent scoops look good and reduce overuse.', improvementOptions: ['Use level scoop to reduce waste', 'Refill station off-peak to cut waste'] },
    { leading: 'Melt managed', text: 'Soft-serve overrun optimal', details: 'Soft-serve texture was on target. You get great taste and more servings per mix.', improvementOptions: ['Calibrate nozzle to reduce waste', 'Purge nightly to prevent waste'] },
    { leading: 'Clean cycles', text: 'Daily machine sanitation on time', details: 'Machines were cleaned on schedule. That prevents faults and keeps quality high.', improvementOptions: ['Set phone reminders to avoid waste', 'Prep sanitizer earlier to reduce waste'] },
  ],
  coffee_shop: [
    { leading: 'Dialled in', text: 'Espresso extraction consistent', details: 'Shot times were steady. Drinks tasted consistent and customers trust the quality.', improvementOptions: ['Purge group head to reduce waste', 'Weigh first shot to cut waste'] },
    { leading: 'Milk mastery', text: 'Milk waste trending lower', details: 'You steamed only what you needed. Less leftover milk means lower cost.', improvementOptions: ['Pitcher size match volume to cut waste', 'Stop over-aerating to reduce waste'] },
    { leading: 'Queue flow', text: 'Order to hand-off faster', details: 'Drinks came out faster. Shorter lines win more walk-up orders.', improvementOptions: ['Stage syrups early to reduce waste', 'Call drinks clearly to avoid remakes'] },
  ],
  italian_restaurant: [
    { leading: 'Pasta perfect', text: 'Cook yield variation reduced', details: 'Pasta was cooked to plan. Stable yield keeps portions and costs on track.', improvementOptions: ['Standardise salt levels to reduce waste', 'Batch weigh portions to cut waste'] },
    { leading: 'Waste watch', text: 'Mozzarella trim waste improved', details: 'Less trim was thrown away. Better cutting keeps more usable product and saves money.', improvementOptions: ['Rotate FIFO daily to reduce waste', 'Store in perforated pans to reduce waste'] },
    { leading: 'Guest love', text: 'Positive appetizer feedback', details: 'Starters were hot and well-timed. Happy guests are more likely to order more.', improvementOptions: ['Pre-fire communication to avoid waste', 'Plate warmers check to reduce waste'] },
  ],
  steak_restaurant: [
    { leading: 'Yield gains', text: 'Butchery trimming efficient', details: 'Careful trimming gave more sellable portions. This protects profit on protein.', improvementOptions: ['Align cut lines to reduce waste', 'Vacuum seal off-cuts to reduce waste'] },
    { leading: 'Cook accuracy', text: 'Doneness accuracy high', details: 'Steaks hit the right doneness first time. Fewer refires, faster service, better experience.', improvementOptions: ['Use probe calibration to avoid waste', 'Rest steaks properly to reduce waste'] },
    { leading: 'Sear speed', text: 'Ticket queue under control', details: 'Grill flow stayed balanced. Shorter dwell times keep orders moving and tables turning.', improvementOptions: ['Stage pans early to reduce waste', 'Batch clarify butter to cut waste'] },
  ],
  carvery_restaurant: [
    { leading: 'Carve control', text: 'Portion size consistency strong', details: 'Portions were even. Fair slices protect margin and guest trust.', improvementOptions: ['Use slicing guide to reduce waste', 'Calibrate scale hourly to cut waste'] },
    { leading: 'Hold quality', text: 'Holding cabinet humidity optimal', details: 'Cabinets stayed at the right humidity. Meat stayed moist longer with less waste.', improvementOptions: ['Rotate roasts sooner to reduce waste', 'Log cabinet checks to prevent waste'] },
    { leading: 'Veg yield', text: 'Root veg waste decreased', details: 'Less veg was lost in prep. Better technique means fuller plates at lower cost.', improvementOptions: ['Use Y-peeler to reduce waste', 'Pre-size cuts to cut waste'] },
  ],
  cocktail_bar: [
    { leading: 'Pour precision', text: 'Free-pour variance minimal', details: 'Pours were consistent. Accurate measures protect liquor cost and taste.', improvementOptions: ['Practice 4-count daily to reduce waste', 'Calibrate spouts to reduce waste'] },
    { leading: 'Garnish game', text: 'Citrus prep aligned to demand', details: 'You prepped the right amount of citrus. Fresher garnishes and less end-of-night waste.', improvementOptions: ['Quarter only midday to cut waste', 'Track weekend pattern to reduce waste'] },
    { leading: 'Speed rail', text: 'Service throughput higher', details: 'Bar setup worked well. More drinks per hour means higher sales per shift.', improvementOptions: ['Stage glassware to reduce waste', 'Batch house syrups to reduce waste'] },
  ],
  pub: [
    { leading: 'Cellar care', text: 'Keg yield improved', details: 'Foam loss was low. More pints per keg and fewer change-outs.', improvementOptions: ['Purge lines fully to reduce waste', 'Maintain correct pressure to reduce waste'] },
    { leading: 'Food uplift', text: 'Snack upsell rate rising', details: 'Team offered snacks well. Higher add-ons lift average spend.', improvementOptions: ['Prompt at ordering to reduce waste', 'Use visual menu cues to reduce waste'] },
    { leading: 'Table turns', text: 'Turn time trending better', details: 'Tables flipped faster. More guests served with the same seats.', improvementOptions: ['Pre-bus proactively to reduce waste', 'Stage cutlery earlier to reduce waste'] },
  ],
};

const IMPROVE_BASE = {
  chicken_qsr: [
    { leading: 'Monitor holding', text: 'Late morning fillet turnover slowing', details: 'Rotate trays sooner or cook smaller batches. Keeps food fresh and avoids waste before lunch.', improvementOptions: ['Schedule mid-hour batch to cut waste', 'Reduce batch size now to reduce waste'] },
    { leading: 'Reduce prep', text: 'Diced lettuce over-prepped', details: 'Prep a smaller batch now. Cuts end-of-day throw-away and frees space.', improvementOptions: ['Shift 20% to PM prep to cut waste', 'Use smaller cambro to reduce waste'] },
  ],
  burger_qsr: [
    { leading: 'Grill cleanup', text: 'Carbon build-up forming', details: 'Quick deglaze now. Hot, clean plates cook faster and safer.', improvementOptions: ['Deglaze between waves to reduce waste', 'Rotate scrapers to reduce waste'] },
    { leading: 'Cheese waste', text: 'Slices drying on station edge', details: 'Keep slices covered and stage fewer. Reduces discard and speeds builds.', improvementOptions: ['Keep wrapped until use to reduce waste', 'Stage fewer at rush to cut waste'] },
  ],
  dessert_qsr: [
    { leading: 'Topping thaw', text: 'Fruit compote thawing too early', details: 'Return half to the freezer. Thaw closer to peak to protect quality.', improvementOptions: ['Return half to freezer to avoid waste', 'Time thaw closer to rush to cut waste'] },
    { leading: 'Cone breakage', text: 'Breakage rate slightly high', details: 'Review handling and storage. Fewer broken cones lowers cost and delays.', improvementOptions: ['Review handling technique to reduce waste', 'Store away from moisture to prevent waste'] },
  ],
  coffee_shop: [
    { leading: 'Grinder drift', text: 'Grind setting tightening needed', details: 'Grind one notch coarser and purge. Brings shot times back and speeds service.', improvementOptions: ['Purge 2 shots to reduce waste', 'Adjust 1 notch coarser to reduce waste'] },
    { leading: 'Milk pitching', text: 'Over-steaming in mid-morning lull', details: 'Use a smaller pitcher or combine orders. Less leftover milk, less cleanup.', improvementOptions: ['Consolidate orders to reduce waste', 'Use 12oz pitcher more to cut waste'] },
  ],
  italian_restaurant: [
    { leading: 'Sauce rotation', text: 'Marinara not stirred frequently', details: 'Stir more often. Prevents scorching and keeps flavour even.', improvementOptions: ['Stir every 10 mins to prevent waste', 'Ladle to smaller bain to reduce waste'] },
    { leading: 'Bread waste', text: 'Excess focaccia par-baked', details: 'Hold back a tray. Bake closer to demand to avoid staling.', improvementOptions: ['Hold 2 trays par-bake to cut waste', 'Freeze surplus earlier to reduce waste'] },
  ],
  steak_restaurant: [
    { leading: 'Thermometer check', text: 'Calibration overdue', details: 'Calibrate the probe now. Accurate temps prevent refires and waste.', improvementOptions: ['Ice bath calibrate now to reduce waste', 'Log in temp sheet to reduce waste'] },
    { leading: 'Trim utilisation', text: 'Excess trimmings unused', details: 'Bag and label trimmings for stock. Turns waste into value.', improvementOptions: ['Vac pack trimmings to reduce waste', 'Schedule stock batch to reduce waste'] },
  ],
  carvery_restaurant: [
    { leading: 'Carve schedule', text: 'Next roast resting overlap risk', details: 'Stagger oven pulls. Leaves space to rest and keeps slices juicy.', improvementOptions: ['Stagger oven pulls to reduce waste', 'Adjust probe alerts to prevent waste'] },
    { leading: 'Pan moisture', text: 'Veg drying on pass', details: 'Add a little moisture and refresh sooner. Keeps veg bright and appetising.', improvementOptions: ['Add small water tray to reduce waste', 'Refresh glaze sooner to reduce waste'] },
  ],
  cocktail_bar: [
    { leading: 'Citrus prep', text: 'Likely over-prep of limes by close', details: 'Halve the next batch. Fresher wedges, less bin at close.', improvementOptions: ['Half batch now to reduce waste', 'Track 30-min depletion to cut waste'] },
    { leading: 'Syrup dating', text: '2 bottles missing labels', details: 'Label syrups now. Clear dates keep quality and audits simple.', improvementOptions: ['Label immediately to reduce waste', 'Audit mise labels to reduce waste'] },
  ],
  pub: [
    { leading: 'Line cleaning', text: 'Next line clean due soon', details: 'Assign the clean today. Better taste and less foaming.', improvementOptions: ['Assign staff member to reduce waste', 'Log after completion to reduce waste'] },
    { leading: 'Glass chill', text: 'Frosted glass rotation uneven', details: 'Rotate chilled glasses front-to-back. Colder pours, happier guests.', improvementOptions: ['Rotate front to back to reduce waste', 'Audit every hour to reduce waste'] },
  ],
};

// Deterministic metric presets per time range
const METRIC_PRESETS: Record<TimeRange, {
  orderlyScore: { value: number; trendPct: number };
  foodWasteReduction: { value: number; trendPct: number }; // proportion reduction vs baseline
  costSavings: { value: number; trendPct: number }; // currency £
  footfall: { value: number; trendPct: number }; // guest count
  sales: { value: number; trendPct: number }; // currency £
  receivingEfficiency: { value: number; trendPct: number }; // proportion
  orderingAccuracy: { value: number; trendPct: number }; // proportion
}> = {
  '7d': {
    orderlyScore: { value: 4.3, trendPct: 3.6 },
    foodWasteReduction: { value: 0.19, trendPct: 2.2 },
    costSavings: { value: 980, trendPct: 1.5 },
    footfall: { value: 3420, trendPct: 1.1 },
    sales: { value: 27400, trendPct: 2.0 },
    receivingEfficiency: { value: 0.91, trendPct: 1.3 },
    orderingAccuracy: { value: 0.88, trendPct: 1.4 },
  },
  '30d': {
    orderlyScore: { value: 4.22, trendPct: 2.4 },
    foodWasteReduction: { value: 0.18, trendPct: 1.8 },
    costSavings: { value: 3914, trendPct: 1.2 },
    footfall: { value: 14800, trendPct: 0.9 },
    sales: { value: 118500, trendPct: 1.6 },
    receivingEfficiency: { value: 0.905, trendPct: 1.0 },
    orderingAccuracy: { value: 0.882, trendPct: 1.1 },
  },
  '60d': {
    orderlyScore: { value: 4.15, trendPct: 1.8 },
    foodWasteReduction: { value: 0.175, trendPct: 1.3 },
    costSavings: { value: 7700, trendPct: 0.9 },
    footfall: { value: 29100, trendPct: 0.7 },
    sales: { value: 232400, trendPct: 1.1 },
    receivingEfficiency: { value: 0.9, trendPct: 0.8 },
    orderingAccuracy: { value: 0.878, trendPct: 0.9 },
  },
  '90d': {
    orderlyScore: { value: 4.05, trendPct: 1.2 },
    foodWasteReduction: { value: 0.17, trendPct: 1.0 },
    costSavings: { value: 11580, trendPct: 0.7 },
    footfall: { value: 43500, trendPct: 0.5 },
    sales: { value: 348900, trendPct: 0.9 },
    receivingEfficiency: { value: 0.898, trendPct: 0.6 },
    orderingAccuracy: { value: 0.875, trendPct: 0.7 },
  },
};

function buildMetrics(range: TimeRange): DashboardData['metrics'] {
  const p = METRIC_PRESETS[range] || METRIC_PRESETS['7d'];
  return [
    {
      label: 'Orderly Score',
      description: 'Your store\'s composite operational performance vs peer median across speed, waste, and execution signals. A higher score reflects consistent process control and adoption of digital guidance, reinforcing sustainable margin gains.',
      value: p.orderlyScore.value,
      max: 5,
      trendPct: p.orderlyScore.trendPct,
      format: v => v.toFixed(1) + '/5',
    },
    {
      label: 'Food waste reduction',
      description: 'Percent reduction in avoidable food waste vs the established baseline period. Sustained reductions directly improve gross margin while signalling stronger prep sizing and holding discipline.',
      value: p.foodWasteReduction.value,
      trendPct: p.foodWasteReduction.trendPct,
      format: v => `${(v * 100).toFixed(1)}%`,
    },
    {
      label: 'Cost savings',
      description: 'Estimated cumulative cost avoided through reduced waste, improved labour allocation, and AI-supported ordering adjustments. These savings can be reinvested into guest experience or margin protection.',
      value: p.costSavings.value,
      trendPct: p.costSavings.trendPct,
      format: v => `£${Math.round(v).toLocaleString()}`,
    },
    {
      label: 'Footfall',
      description: 'Total guest visits captured in the selected period. Monitoring footfall alongside conversion and speed helps staff anticipate staffing pivots and promotional impacts.',
      value: p.footfall.value,
      trendPct: p.footfall.value,
      format: v => v.toLocaleString(),
    },
    {
      label: 'Sales',
      description: 'Net sales (ex VAT) for the period. Growth paired with stable or improving waste and labour indicators shows quality scaling rather than cost drift.',
      value: p.sales.value,
      trendPct: p.sales.trendPct,
      format: v => `£${Math.round(v).toLocaleString()}`,
    },
    {
      label: 'Receiving efficiency',
      description: 'Percent of deliveries processed without discrepancy or rework. Efficient receiving reduces shrink risk and accelerates inventory availability for production planning.',
      value: p.receivingEfficiency.value,
      trendPct: p.receivingEfficiency.trendPct,
      format: v => `${(v * 100).toFixed(1)}%`,
    },
    {
      label: 'Ordering optimisation (AI)',
      description: 'Accuracy of stock ordering vs actual demand after AI recommendations. Better optimisation lowers emergency purchases and avoids excess tying up working capital.',
      value: p.orderingAccuracy.value,
      trendPct: p.orderingAccuracy.trendPct,
      format: v => `${(v * 100).toFixed(1)}%`,
    },
  ];
}

// Re-add helper utilities lost during truncation
interface ProgressItem {
  leading: string;
  text: string;
  details: string;
  improvementOptions: string[];
}

interface ProgressItemWithId extends ProgressItem {
  id: string;
}

function withIds(items: ProgressItem[]): ProgressItemWithId[] { return items.map(i => ({ ...i, id: (i.leading + '-' + i.text).toLowerCase().replace(/[^a-z0-9]+/g, '-') })); }

function buildProgressItems(storeType: SimulationOptionsPerformance['storeType'], category: string, currentHour: number): DashboardData['progress'] {
  const good = withIds(GOOD_BASE[storeType]);
  const improve = withIds(IMPROVE_BASE[storeType]);
  return { good, improve };
}

function buildSimulatedPerformance(opts: SimulationOptionsPerformance, range: TimeRange) {
  const { storeType, currentHour } = opts;
  const category = CATEGORY_MAP[storeType];
  const metrics = buildMetrics(range);
  const progress = buildProgressItems(storeType, category, currentHour);
  return { metrics, progress };
}

export function useDashboardPerformanceData(range: TimeRange, simulationOptions?: SimulationOptionsPerformance) {
  const [metrics, setMetrics] = useState<DashboardData['metrics'] | undefined>();
  const [progress, setProgress] = useState<DashboardData['progress'] | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setError(null);
      try {
        if (simulationOptions?.simulation) {
          const sim = buildSimulatedPerformance(simulationOptions, range);
          if (alive) { setMetrics(sim.metrics); setProgress(sim.progress); }
          return;
        }
        if (alive) {
          setMetrics(buildMetrics(range));
          const fallbackStoreType: SimulationOptionsPerformance['storeType'] = (simulationOptions?.storeType) || 'chicken_qsr';
          const category = CATEGORY_MAP[fallbackStoreType];
          setProgress(buildProgressItems(fallbackStoreType, category, simulationOptions?.currentHour ?? new Date().getHours()));
        }
      } catch (e: unknown) { if (alive) setError((e as Error).message || 'Failed to load performance data'); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [range, simulationOptions?.simulation, simulationOptions?.storeType, simulationOptions?.currentHour]);

  return { metrics, progress, loading, error };
}